import { HttpClient } from '@angular/common/http';
import { computed, Injectable, signal, Signal, WritableSignal } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { NotificationType } from 'angular-to-phaser';
import { Observable, catchError, map, of, retry, tap } from 'rxjs';
import { environment } from 'src/environments/environment';
import { Notification } from '../dto';
import { AbstractService } from './abstractservice';
import { ApplicationService } from './application.service';

@Injectable({
    providedIn: 'root'
})
export class NotificationService extends AbstractService {

    private readonly privateNotificationsState: WritableSignal<Notification[]> = signal([]);
    public readonly privateNotificationsSignal: Signal<Notification[]> = this.privateNotificationsState.asReadonly();
    public readonly persistentPrivateNotifications: Observable<Notification[]> = toObservable(this.privateNotificationsState);
    public readonly unreadPrivateNotificationsSignal: Signal<Notification[]> = computed(() =>
        this.privateNotificationsState().filter(n => !n.ReadDate && (
            n.NotificationType == NotificationType.FriendshipRequest || n.NotificationType == NotificationType.PrivateMessage)));
    // Split back out of unreadPrivateNotifications for consumers that need to distinguish the
    // two - e.g. a friend request has no other always-visible indicator anywhere in the app,
    // but a private message already does (the floating chat panel's own unread badge), so
    // anything summarizing "what needs your attention that you can't already see" should count
    // requests but not messages.
    public readonly unreadFriendRequestsSignal: Signal<Notification[]> = computed(() =>
        this.privateNotificationsState().filter(n => !n.ReadDate && n.NotificationType == NotificationType.FriendshipRequest));
    public readonly unreadPrivateMessagesSignal: Signal<Notification[]> = computed(() =>
        this.privateNotificationsState().filter(n => !n.ReadDate && n.NotificationType == NotificationType.PrivateMessage));

    private readonly messageboardNotificationsState: WritableSignal<Notification[]> = signal([]);
    public readonly persistentMessageboardNotifications: Observable<Notification[]> = toObservable(this.messageboardNotificationsState);
    public readonly unreadMessageboardNotificationsSignal: Signal<Notification[]> = computed(() =>
        this.messageboardNotificationsState().filter(n => !n.ReadDate && n.NotificationType == NotificationType.MessageBoard));

    constructor(private http: HttpClient, private coreAppService: ApplicationService) {
        super(coreAppService.authService, coreAppService.featureFlagService);

        var authSubscription = coreAppService.userService.activeUser$.subscribe(user => {
            if (user && user.Username && this.authService.isAuthenticated()) {
                this.coreAppService.notificationService.initializeConnection();
                this.retrieveUnreadNotifications([NotificationType.PrivateMessage, NotificationType.FriendshipRequest], this.privateNotificationsState);
                this.retrieveUnreadNotifications([NotificationType.MessageBoard], this.messageboardNotificationsState);
            }
        });

        // Notifications pushed while the SignalR connection was down never arrive live.
        coreAppService.notificationService.reconnected$.subscribe(() => {
            this.retrieveUnreadNotifications([NotificationType.PrivateMessage, NotificationType.FriendshipRequest], this.privateNotificationsState);
            this.retrieveUnreadNotifications([NotificationType.MessageBoard], this.messageboardNotificationsState);
        });
    }


    public retrieveUnreadNotifications(types: NotificationType[], updateSignal: WritableSignal<Notification[]>) {
        this.http.get<Notification[]>(`${environment.apiUrl}/notification/unread/?type=${types.map(t => t.valueOf()).join('&type=')}`, this.httpOptionsAuthJson())
            .pipe(
                retry(1),
                map(notifications => notifications.map(n => new Notification(n, this.coreAppService.userService.activeUser$.getValue().Username, this.coreAppService.jsonMapper.jsonEnricher()))),
                catchError(this.handleError),
            ).subscribe(sendResponse => {
                this.insertNotifications(sendResponse, updateSignal);
            });
    }

    public markNotificationsAsRead(...readNotifications: Notification[]) {
        if (!readNotifications?.length || readNotifications.findIndex(n => !n) >= 0) return of();
        return this.http.patch(environment.apiUrl + '/notification/multi/', readNotifications.map(n => n.Id), this.httpOptionsAuthJson())
            .pipe(
                retry(1),
                catchError(this.handleError),
                map(() => readNotifications.map(n => new Notification(n, this.coreAppService.userService.activeUser$.getValue().Username, this.coreAppService.jsonMapper.jsonEnricher()))),
                tap({
                    next: () => {
                        let updateSignal = this.determineUpdateSubject(readNotifications[0].NotificationType);
                        readNotifications.forEach(n => n.ReadDate = new Date());
                        this.insertNotifications(readNotifications, updateSignal);
                    }
                }));
    }

    public addNotification(notification: Notification) {
        if (!notification) return;
        let updateSignal = this.determineUpdateSubject(notification.NotificationType);
        this.insertNotifications([notification], updateSignal)
    }

    public removeNotification(notification: Notification) {
        if (!notification) return;
        let updateSignal = this.determineUpdateSubject(notification.NotificationType);
        this.removeNotifications([notification], updateSignal)
    }

    private determineUpdateSubject(notificationType: NotificationType): WritableSignal<Notification[]> {
        switch (notificationType) {
            case NotificationType.FriendshipRequest:
            case NotificationType.PrivateMessage: return this.privateNotificationsState; break;
            case NotificationType.MessageBoard: return this.messageboardNotificationsState; break;
            default: throw "Invalid NotificationType!";
        }
    }

    private insertNotifications(notifications: Notification[], updateSignal: WritableSignal<Notification[]>) {
        var oldNotes = updateSignal();
        var oldNotes = oldNotes.filter(old => notifications.findIndex(newNote => newNote.Id == old.Id) === -1);  //no duplicates
        oldNotes.push(...notifications);
        oldNotes.sort((a, b) => a.CreateDate.valueOf() - b.CreateDate.valueOf());
        updateSignal.set(oldNotes);
    }

    private removeNotifications(notifications: Notification[], updateSignal: WritableSignal<Notification[]>) {
        var oldNotes = updateSignal();
        var oldNotes = oldNotes.filter(old => notifications.findIndex(newNote => newNote.Id == old.Id) === -1);  //remove matches
        oldNotes.sort((a, b) => a.CreateDate.valueOf() - b.CreateDate.valueOf());
        updateSignal.set(oldNotes);
    }




}
